import { KeyRound, Plus, UserCog } from 'lucide-react'
import { useState } from 'react'
import { ApiError } from '../../api/client'
import { useCreateUser, useMe, useTeams, useUpdateUser, useUsers } from '../../api/hooks'
import type { AppUser, Role } from '../../api/types'
import { Badge, Button, Card, EmptyState, Field, Input, Modal, PageHeader, Select, TableSkeleton, Toggle } from '../../components/ui'
import { useToast } from '../../components/ui/toast'
import { fmtRelative, ROLE_LABEL } from '../../lib/format'

const ROLE_TONE: Record<Role, 'navy' | 'neutral' | 'success' | 'warning'> = { SUPERADMIN: 'warning', ADMIN: 'navy', PROJEKTLEDARE: 'navy', RADGIVARE: 'neutral' }
const PROVIDER_LABEL: Record<string, string> = { google: 'Google', microsoft: 'Microsoft', dev: 'Lokalt' }

export function UsersPage() {
  const me = useMe()
  const users = useUsers()
  const teams = useTeams()
  const create = useCreateUser()
  const update = useUpdateUser()
  const toast = useToast()
  const [editing, setEditing] = useState<AppUser | null>(null)
  const [adding, setAdding] = useState(false)
  const isSuper = me.data?.role === 'SUPERADMIN'
  const roles: Role[] = isSuper ? ['SUPERADMIN', 'ADMIN', 'PROJEKTLEDARE', 'RADGIVARE'] : ['ADMIN', 'PROJEKTLEDARE', 'RADGIVARE']
  const list = users.data ?? []
  const fail = (e: unknown) => toast.push('error', e instanceof ApiError ? String(e.detail) : 'Något gick fel')

  return (
    <>
      <PageHeader
        title="Användare"
        subtitle="Vem som får logga in och vad de får göra. Konton skapas automatiskt vid första inloggningen; lägg till i förväg för att styra rollen från start."
        actions={<Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setAdding(true)}>Lägg till användare</Button>}
      />

      <Card padded={false}>
        {users.isLoading ? <TableSkeleton rows={6} cols={6} /> : list.length === 0 ? (
          <EmptyState icon={<UserCog className="size-6" />} title="Inga användare ännu" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="table-head"><tr><th>Namn</th><th>Roll</th><th>Team</th><th>Inloggning</th><th>Senast inloggad</th><th className="text-right">Status</th></tr></thead>
              <tbody>
                {list.map((u) => (
                  <tr key={u.id} onClick={() => setEditing(u)} className={`table-row cursor-pointer ${!u.is_active ? 'opacity-50' : ''}`}>
                    <td><span className="block font-medium text-ink-900">{u.name}</span><span className="block text-xs text-ink-500">{u.email}</span></td>
                    <td>
                      <span className="inline-flex items-center gap-1.5">
                        <Badge tone={ROLE_TONE[u.role]}>{ROLE_LABEL[u.role]}</Badge>
                        {u.role_from_idp && <span title="Rollen styrs av Entra ID"><KeyRound className="size-3.5 text-ink-400" /></span>}
                      </span>
                    </td>
                    <td className="text-ink-700">{u.team_label ?? (u.role === 'RADGIVARE' ? <span className="text-warning-700">Saknar team</span> : '–')}</td>
                    <td className="text-ink-500">{u.provider ? PROVIDER_LABEL[u.provider] ?? u.provider : <span className="text-ink-400">Har inte loggat in</span>}</td>
                    <td className="text-ink-500">{u.last_login_at ? fmtRelative(u.last_login_at) : '–'}</td>
                    <td className="text-right">{u.is_active ? <Badge tone="success" dot>Aktiv</Badge> : <Badge tone="neutral">Inaktiv</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="mt-4 text-xs leading-relaxed text-ink-500">
        Solvigo-konton (Google, solvigo.ai) blir superadministratörer automatiskt. Söderberg &amp; Partners-konton (Microsoft) blir administratörer om inget annat anges här eller via app-roller i Entra ID. Ändringar loggas och loggar ut den berörda användaren.
      </p>

      {editing && (
        <EditUserModal
          user={editing}
          roles={roles}
          teams={teams.data ?? []}
          selfId={me.data?.id}
          pending={update.isPending}
          onClose={() => setEditing(null)}
          onSave={(patch) => update.mutate({ id: editing.id, ...patch }, { onSuccess: () => { toast.push('success', 'Användaren är uppdaterad'); setEditing(null) }, onError: fail })}
        />
      )}
      {adding && (
        <AddUserModal
          roles={roles.filter((r) => r !== 'SUPERADMIN')}
          teams={teams.data ?? []}
          pending={create.isPending}
          onClose={() => setAdding(false)}
          onSave={(data) => create.mutate(data, { onSuccess: () => { toast.push('success', 'Användaren är tillagd'); setAdding(false) }, onError: fail })}
        />
      )}
    </>
  )
}

type TeamOpt = { id: string; code: string; name: string; is_active: boolean }

function TeamSelect({ value, onChange, teams }: { value: string; onChange: (v: string) => void; teams: TeamOpt[] }) {
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Inget team</option>
      {teams.filter((t) => t.is_active).map((t) => <option key={t.id} value={t.id}>{t.code} {t.name}</option>)}
    </Select>
  )
}

function EditUserModal({ user, roles, teams, selfId, pending, onClose, onSave }: {
  user: AppUser; roles: Role[]; teams: TeamOpt[]; selfId?: string; pending: boolean; onClose: () => void
  onSave: (patch: { role?: Role; team_id?: string | null; clear_team?: boolean; is_active?: boolean }) => void
}) {
  const [role, setRole] = useState<Role>(user.role)
  const [team, setTeam] = useState(user.team_id ?? '')
  const [active, setActive] = useState(user.is_active)
  const isSelf = user.id === selfId
  const canEditRole = !user.role_from_idp && !isSelf && roles.includes(user.role)
  const save = () => onSave({
    ...(role !== user.role ? { role } : {}),
    ...(team !== (user.team_id ?? '') ? (team ? { team_id: team } : { clear_team: true }) : {}),
    ...(active !== user.is_active ? { is_active: active } : {}),
  })
  return (
    <Modal open onClose={onClose} title={user.name} footer={<><Button onClick={onClose}>Avbryt</Button><Button variant="primary" loading={pending} onClick={save}>Spara</Button></>}>
      <p className="mb-4 text-sm text-ink-500">{user.email}</p>
      <div className="space-y-4">
        <Field label="Roll" hint={user.role_from_idp ? 'Rollen kommer från en app-roll i Entra ID och ändras där.' : isSelf ? 'Du kan inte ändra din egen roll.' : undefined}>
          <Select value={role} onChange={(e) => setRole(e.target.value as Role)} disabled={!canEditRole}>
            {(roles.includes(user.role) ? roles : [user.role, ...roles]).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
          </Select>
        </Field>
        <Field label="Team" hint={role === 'RADGIVARE' ? 'Rådgivare ser bara detta teams kunder och anmälningar.' : 'Behövs bara för rådgivare.'}>
          <TeamSelect value={team} onChange={setTeam} teams={teams} />
        </Field>
        <Toggle checked={active} onChange={setActive} label={isSelf ? 'Aktiv (du kan inte inaktivera dig själv)' : 'Aktiv – får logga in'} />
      </div>
    </Modal>
  )
}

function AddUserModal({ roles, teams, pending, onClose, onSave }: {
  roles: Role[]; teams: TeamOpt[]; pending: boolean; onClose: () => void
  onSave: (data: { name: string; email: string; role: Role; team_id?: string | null }) => void
}) {
  const [f, setF] = useState<{ name: string; email: string; role: Role; team: string }>({ name: '', email: '', role: 'PROJEKTLEDARE', team: '' })
  const valid = f.name.trim().length > 0 && /.+@.+\..+/.test(f.email)
  return (
    <Modal open onClose={onClose} title="Lägg till användare" footer={<><Button onClick={onClose}>Avbryt</Button><Button variant="primary" disabled={!valid} loading={pending} onClick={() => onSave({ name: f.name.trim(), email: f.email.trim(), role: f.role, team_id: f.team || null })}>Lägg till</Button></>}>
      <p className="mb-4 text-sm text-ink-500">Personen loggar in som vanligt med sitt jobbkonto och får då den här rollen direkt.</p>
      <div className="space-y-4">
        <Field label="Namn"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Förnamn Efternamn" /></Field>
        <Field label="E-post (jobbkontot)"><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="namn@soderbergpartners.se" /></Field>
        <Field label="Roll">
          <Select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value as Role })}>
            {roles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
          </Select>
        </Field>
        {f.role === 'RADGIVARE' && (
          <Field label="Team" hint="Matchas annars automatiskt mot rådgivarens e-post i Dynamics vid första inloggningen.">
            <TeamSelect value={f.team} onChange={(v) => setF({ ...f, team: v })} teams={teams} />
          </Field>
        )}
      </div>
    </Modal>
  )
}
